/**
 * Tâche 1147 (Eric 2026-06-16) — Page publique de désabonnement.
 *
 * Le lien vient d'un courriel : la personne n'a ni session, ni cookie CSRF, et
 * n'a parfois pas de compte Knoria. Elle voit tout ce que son adresse reçoit,
 * décoche ce qu'elle ne veut plus, et c'est fini — demander d'ouvrir
 * l'interface d'administration, c'est demander un effort que personne ne fait.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const tokenFromPath = () => {
  const parts = window.location.pathname.split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

export default function UnsubscribePage() {
  const [token] = useState(tokenFromPath);
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/public/unsubscribe/${token}`);
      setState(res.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Ce lien de désabonnement n'est plus valide."
      );
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const key = (s) => `${s.org_id}|${s.family}`;
  const toggle = (s) =>
    setSelected((prev) =>
      prev.includes(key(s)) ? prev.filter((k) => k !== key(s)) : [...prev, key(s)]
    );

  const submit = async () => {
    setBusy(true);
    try {
      const families = selected.map((k) => {
        const [org_id, family] = k.split("|");
        return { org_id, family };
      });
      const res = await axios.post(
        `${API}/api/public/unsubscribe/${token}`,
        { families }
      );
      setDone(res.data);
      setState({ email: res.data.email, subscriptions: res.data.remaining });
      setSelected([]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Le retrait n'a pas pu être enregistré.");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-6">
        <div
          className="max-w-md rounded-xl border border-amber-300 bg-amber-50 p-6"
          data-testid="unsubscribe-error"
        >
          <Warning size={24} className="text-amber-600" />
          <h1 className="mt-2 text-lg font-semibold text-amber-900">
            Lien invalide
          </h1>
          <p className="mt-1 text-sm text-amber-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!state) return null;

  return (
    <div className="min-h-screen bg-zinc-50 px-6 py-12">
      <div
        className="mx-auto max-w-2xl rounded-2xl border border-zinc-200 bg-white p-7"
        data-testid="unsubscribe-page"
      >
        <h1 className="text-2xl font-semibold text-zinc-900">
          Gérer mes courriels Knoria
        </h1>
        <p className="mt-1 text-sm text-zinc-600" data-testid="unsubscribe-email">
          Adresse : <strong>{state.email}</strong>
        </p>

        {done && (
          <div
            className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3"
            data-testid="unsubscribe-done"
          >
            <CheckCircle size={18} className="mt-0.5 text-emerald-600" />
            <p className="text-sm text-emerald-900">
              {done.removed?.length
                ? `Retiré de ${done.removed.length} liste(s). Vous ne recevrez plus ces courriels.`
                : "Aucun changement à enregistrer."}
            </p>
          </div>
        )}

        {state.subscriptions?.length ? (
          <>
            <p className="mt-5 text-sm text-zinc-700">
              Cochez ce que vous ne voulez plus recevoir :
            </p>
            <div className="mt-3 space-y-2" data-testid="unsubscribe-list">
              {state.subscriptions.map((s) => (
                <label
                  key={key(s)}
                  className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-zinc-200 px-3 py-2 hover:border-zinc-300"
                  data-testid={`unsubscribe-item-${s.org_id}-${s.family}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(key(s))}
                    onChange={() => toggle(s)}
                    className="mt-1 h-4 w-4 rounded border-zinc-300 text-[#002FA7]"
                  />
                  <span>
                    <span className="text-sm font-medium text-zinc-900">
                      {s.family_label}
                    </span>
                    <span className="block text-xs text-zinc-500">
                      {s.org_name}
                      {s.active ? "" : " · envois actuellement en pause"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={submit}
                disabled={busy || !selected.length}
                className="px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-medium disabled:opacity-50"
                data-testid="unsubscribe-submit"
              >
                Me retirer des envois cochés
              </button>
              <button
                type="button"
                onClick={() =>
                  setSelected(state.subscriptions.map((s) => key(s)))
                }
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 disabled:opacity-50"
                data-testid="unsubscribe-select-all"
              >
                Tout cocher
              </button>
            </div>
          </>
        ) : (
          <p
            className="mt-5 text-sm text-zinc-600"
            data-testid="unsubscribe-empty"
          >
            Cette adresse ne reçoit plus aucun courriel automatique de Knoria.
          </p>
        )}

        <p className="mt-6 text-xs text-zinc-400">
          Ce lien ne permet que de vous retirer : il ne peut inscrire personne.
        </p>
      </div>
    </div>
  );
}
